import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * ModalMoveArticles — Déplacement EN LOT de prix price-eat d'un écran vers un autre.
 *
 * Contexte videowall (cf. player SIGNAGE-WEBAPP/src/overlay/OverlayLayer.ts) :
 *  - Le LABEL prix est rendu en pixels absolus sur le canvas du mur : left = position_x × 38,
 *    top = position_y × 38. Un mur 2×2 en 4K (3840×2160), dalles FHD (1920×1080), donne donc
 *    un repère global : X 0→101, Y 0→57 ; chaque dalle = (1920/38)=50.5 large × (1080/38)=28.4 haut.
 *  - L'ICÔNE rupture, elle, est TOUJOURS positionnée en % de la LARGEUR (même en videowall) :
 *    left = largeur × position_icon_x/100, top = largeur × position_icon_y/100.
 *    -> offset icône exprimé en % de la largeur du mur (unité différente du label, géré à part).
 *
 * Déplacer un prix d'une dalle à sa voisine = ajouter à position_x/y le décalage d'une dalle.
 * On réutilise l'endpoint existant updateArticle (aucune modif du back price-eat).
 */

const DIV38 = 38; // constante de rendu videowall du player (px = valeur × 38)

const pf = (v, d = 0) => { const n = parseFloat(v); return isNaN(n) ? d : n; };
const r1 = (n) => Math.round(n * 10) / 10;   // 1 décimale, comme le clic-position existant
const r2 = (n) => Math.round(n * 100) / 100; // 2 décimales pour les % d'icône

const ModalMoveArticles = ({ isVisible, onClose, articles, idPdv }) => {
  const token = localStorage.getItem('token');
  const headers = { 'x-access-token': token, 'Content-Type': 'application/json' };

  // --- Config mur (défauts : 2×2, dalles FHD) ---
  const [cols, setCols] = useState(2);
  const [rows, setRows] = useState(2);
  const [dalleW, setDalleW] = useState(1920);
  const [dalleH, setDalleH] = useState(1080);

  // --- Mapping cellule (grille) -> display_id de l'écran (éditable) ---
  const uniqueIds = useMemo(
    () => [...new Set((articles || []).map((a) => String(a.display_id)).filter(Boolean))]
      .sort((a, b) => (isNaN(a) || isNaN(b) ? a.localeCompare(b) : Number(a) - Number(b))),
    [articles]
  );
  const nbCells = cols * rows;
  const [cellIds, setCellIds] = useState([]);
  useEffect(() => {
    // Par défaut : row-major (1=TL, 2=TR, 3=BL, 4=BR), pré-rempli avec les display_id existants.
    setCellIds(Array.from({ length: nbCells }, (_, i) => (uniqueIds[i] ?? String(i + 1))));
  }, [nbCells, uniqueIds]);

  const [sourceCell, setSourceCell] = useState(0);
  const [targetCell, setTargetCell] = useState(1);
  // Garde : si la grille rétrécit, on garde des index de cellules valides.
  useEffect(() => {
    setSourceCell((c) => Math.min(c, nbCells - 1));
    setTargetCell((c) => Math.min(c, nbCells - 1));
  }, [nbCells]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [reassign, setReassign] = useState(true);   // réaffecter display_id à l'écran cible
  const [moveIcon, setMoveIcon] = useState(true);    // décaler aussi l'icône rupture
  const [manualX, setManualX] = useState('');        // override offset X (unités label)
  const [manualY, setManualY] = useState('');
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);

  const sourceDisplayId = cellIds[sourceCell] ?? '';
  const targetDisplayId = cellIds[targetCell] ?? '';

  // Articles de l'écran source
  const sourceArticles = useMemo(
    () => (articles || []).filter((a) => String(a.display_id) === String(sourceDisplayId)),
    [articles, sourceDisplayId]
  );
  // À chaque changement de source : tout sélectionner par défaut.
  useEffect(() => {
    setSelectedIds(new Set(sourceArticles.map((a) => a.Id)));
  }, [sourceArticles]);

  // --- Offsets calculés depuis la grille ---
  const dCol = (targetCell % cols) - (sourceCell % cols);
  const dRow = Math.floor(targetCell / cols) - Math.floor(sourceCell / cols);
  const wallW = cols * dalleW;

  const autoOffX = r1((dCol * dalleW) / DIV38);   // label : px/38
  const autoOffY = r1((dRow * dalleH) / DIV38);
  const offX = manualX !== '' ? pf(manualX) : autoOffX;
  const offY = manualY !== '' ? pf(manualY) : autoOffY;

  const iconOffX = r2((dCol * dalleW) / wallW * 100); // icône : % de LARGEUR
  const iconOffY = r2((dRow * dalleH) / wallW * 100); // (top icône aussi rapporté à la largeur, cf. player)

  // Bornes du mur en unités label (pour signaler un débordement)
  const maxX = r1(wallW / DIV38);
  const maxY = r1((rows * dalleH) / DIV38);

  const preview = useMemo(() => sourceArticles
    .filter((a) => selectedIds.has(a.Id))
    .map((a) => {
      const nx = r1(pf(a.position_x) + offX);
      const ny = r1(pf(a.position_y) + offY);
      return {
        Id: a.Id, name: a.name,
        ox: pf(a.position_x), oy: pf(a.position_y), nx, ny,
        out: nx < 0 || ny < 0 || nx > maxX || ny > maxY,
      };
    }), [sourceArticles, selectedIds, offX, offY, maxX, maxY]);

  const toggle = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const handleApply = async () => {
    if (preview.length === 0) { toast.warn('Aucun prix sélectionné.'); return; }
    if (sourceCell === targetCell && !reassign && offX === 0 && offY === 0) {
      toast.warn('Source = cible et aucun décalage : rien à faire.'); return;
    }
    if (reassign && !String(targetDisplayId).trim()) {
      toast.error('Display_id cible vide : renseignez-le dans la grille.'); return;
    }
    setApplying(true);
    setProgress(0);
    const updateUrl = `${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/auth/user/updateArticle/PriceEat`;
    let ok = 0, ko = 0;
    for (const a of sourceArticles) {
      if (!selectedIds.has(a.Id)) continue;
      const body = {
        ...a,
        token,
        display_id: reassign ? String(targetDisplayId) : a.display_id,
        position_x: String(r1(pf(a.position_x) + offX)),
        position_y: String(r1(pf(a.position_y) + offY)),
      };
      if (moveIcon) {
        body.position_icon_x = String(r2(pf(a.position_icon_x) + iconOffX));
        body.position_icon_y = String(r2(pf(a.position_icon_y) + iconOffY));
      }
      try {
        const res = await axios.post(updateUrl, body, { headers });
        (res.status === 200) ? ok++ : ko++;
      } catch (e) { ko++; console.error('move article KO', a.Id, e); }
      setProgress((p) => p + 1);
    }
    // Notifie le(s) device(s) de re-télécharger les prix (un seul appel après le lot).
    try {
      await axios.post(
        `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/retrivePinCode`,
        { idPdv }, { headers }
      );
    } catch (e) { console.error('retrivePinCode KO', e); }

    setApplying(false);
    if (ko === 0) {
      toast.success(`${ok} prix déplacé(s) vers l'écran ${targetDisplayId}. Rechargement…`);
      setTimeout(() => window.location.reload(false), 1200);
    } else {
      toast.error(`${ok} OK, ${ko} en échec. Vérifiez la console.`);
    }
  };

  if (!isVisible) return null;

  const cellLabel = (i) => (i === sourceCell ? 'Source' : i === targetCell ? 'Cible' : '');
  const cellCls = (i) =>
    'relative border rounded-lg h-20 flex flex-col items-center justify-center cursor-pointer transition-colors ' +
    (i === sourceCell ? 'bg-blue-600 text-white border-blue-700 '
      : i === targetCell ? 'bg-green-600 text-white border-green-700 '
      : 'bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-700 ');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-start overflow-y-auto z-50 py-8"
      id="move-wrapper" onClick={(e) => { if (e.target.id === 'move-wrapper') onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-800">Déplacer des prix en lot</h2>
          <button className="text-slate-500 hover:text-slate-800 text-2xl leading-none" onClick={onClose}>×</button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* ---- Colonne gauche : config + grille ---- */}
          <div>
            {/* Config mur */}
            <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-2 text-sm">Configuration du mur</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="flex flex-col">Colonnes
                  <input type="number" min="1" value={cols} onChange={(e) => setCols(Math.max(1, +e.target.value || 1))}
                    className="border rounded px-2 py-1 mt-1" /></label>
                <label className="flex flex-col">Lignes
                  <input type="number" min="1" value={rows} onChange={(e) => setRows(Math.max(1, +e.target.value || 1))}
                    className="border rounded px-2 py-1 mt-1" /></label>
                <label className="flex flex-col">Largeur dalle (px)
                  <input type="number" value={dalleW} onChange={(e) => setDalleW(+e.target.value || 0)}
                    className="border rounded px-2 py-1 mt-1" /></label>
                <label className="flex flex-col">Hauteur dalle (px)
                  <input type="number" value={dalleH} onChange={(e) => setDalleH(+e.target.value || 0)}
                    className="border rounded px-2 py-1 mt-1" /></label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                1 dalle = {r1(dalleW / DIV38)} unités en X, {r1(dalleH / DIV38)} en Y (px ÷ 38). Mur : {maxX} × {maxY}.
              </p>
            </div>

            {/* Grille cliquable source -> cible */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-2 text-sm">
                Cliquez l'écran <span className="text-blue-600">source</span>, puis l'écran <span className="text-green-600">cible</span>
              </h3>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
                {Array.from({ length: nbCells }, (_, i) => (
                  <div key={i} className={cellCls(i)}
                    onClick={() => { setSourceCell(i); if (i === targetCell) setTargetCell(cellIds.findIndex((_, k) => k !== i)); }}
                    onContextMenu={(e) => { e.preventDefault(); if (i !== sourceCell) setTargetCell(i); }}>
                    <span className="text-[10px] uppercase opacity-70">{cellLabel(i)}</span>
                    <input value={cellIds[i] ?? ''} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setCellIds((p) => p.map((v, k) => (k === i ? e.target.value : v)))}
                      className="w-12 text-center bg-white/80 text-slate-800 rounded text-sm border border-slate-300 mt-1" />
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <label className="flex flex-col text-slate-600">Écran source
                  <select value={sourceCell}
                    onChange={(e) => { const v = +e.target.value; setSourceCell(v); if (v === targetCell) setTargetCell(cellIds.findIndex((_, k) => k !== v)); }}
                    className="border rounded px-2 py-1 mt-1 bg-white">
                    {Array.from({ length: nbCells }, (_, i) => (
                      <option key={i} value={i}>Case {i + 1} — display {cellIds[i] ?? ''}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col text-slate-600">Écran cible
                  <select value={targetCell} onChange={(e) => setTargetCell(+e.target.value)}
                    className="border rounded px-2 py-1 mt-1 bg-white">
                    {Array.from({ length: nbCells }, (_, i) => (
                      <option key={i} value={i} disabled={i === sourceCell}>Case {i + 1} — display {cellIds[i] ?? ''}</option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Sélecteurs ci-dessus, ou dans la grille : clic = <span className="text-blue-600">source</span>, clic droit = <span className="text-green-600">cible</span>. Le nombre dans chaque case = son <b>display_id</b>.
              </p>
            </div>

            {/* Offset calculé + override */}
            <div className="mt-4 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-slate-700">Décalage appliqué :</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">X {offX >= 0 ? '+' : ''}{offX}</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">Y {offY >= 0 ? '+' : ''}{offY}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col text-xs text-slate-600">Override X (unités)
                  <input type="number" step="0.1" placeholder={`auto ${autoOffX}`} value={manualX}
                    onChange={(e) => setManualX(e.target.value)} className="border rounded px-2 py-1 mt-1" /></label>
                <label className="flex flex-col text-xs text-slate-600">Override Y (unités)
                  <input type="number" step="0.1" placeholder={`auto ${autoOffY}`} value={manualY}
                    onChange={(e) => setManualY(e.target.value)} className="border rounded px-2 py-1 mt-1" /></label>
              </div>
              <div className="flex flex-col gap-1 mt-3">
                <label className="flex items-center gap-2 text-slate-700">
                  <input type="checkbox" checked={reassign} onChange={(e) => setReassign(e.target.checked)} />
                  Réaffecter le display_id à l'écran cible ({targetDisplayId})
                </label>
                <label className="flex items-center gap-2 text-slate-700">
                  <input type="checkbox" checked={moveIcon} onChange={(e) => setMoveIcon(e.target.checked)} />
                  Décaler aussi l'icône rupture (X {iconOffX >= 0 ? '+' : ''}{iconOffX}% · Y {iconOffY >= 0 ? '+' : ''}{iconOffY}%)
                </label>
              </div>
            </div>
          </div>

          {/* ---- Colonne droite : sélection + aperçu ---- */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-700 text-sm">
                Prix de l'écran source {sourceDisplayId} ({sourceArticles.length})
              </h3>
              <div className="text-xs">
                <button className="text-blue-600 hover:underline mr-2"
                  onClick={() => setSelectedIds(new Set(sourceArticles.map((a) => a.Id)))}>Tout</button>
                <button className="text-blue-600 hover:underline"
                  onClick={() => setSelectedIds(new Set())}>Aucun</button>
              </div>
            </div>

            <div className="border rounded-xl max-h-[26rem] overflow-auto">
              <table className="w-full min-w-max text-sm">
                <thead className="bg-slate-700 text-white sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Nom</th>
                    <th className="p-2">X</th>
                    <th className="p-2">→ X</th>
                    <th className="p-2">Y</th>
                    <th className="p-2">→ Y</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceArticles.length === 0 && (
                    <tr><td colSpan="6" className="p-4 text-center text-slate-400">Aucun prix sur cet écran.</td></tr>
                  )}
                  {sourceArticles.map((a) => {
                    const sel = selectedIds.has(a.Id);
                    const nx = r1(pf(a.position_x) + offX);
                    const ny = r1(pf(a.position_y) + offY);
                    const out = nx < 0 || ny < 0 || nx > maxX || ny > maxY;
                    return (
                      <tr key={a.Id} className={`border-b ${sel ? '' : 'opacity-40'} ${out ? 'bg-red-50' : ''}`}>
                        <td className="p-2 text-center">
                          <input type="checkbox" checked={sel} onChange={() => toggle(a.Id)} /></td>
                        <td className="p-2 text-left">{a.name}</td>
                        <td className="p-2 text-center text-slate-500">{pf(a.position_x)}</td>
                        <td className="p-2 text-center font-semibold">{nx}</td>
                        <td className="p-2 text-center text-slate-500">{pf(a.position_y)}</td>
                        <td className="p-2 text-center font-semibold">{ny}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {preview.some((p) => p.out) && (
              <p className="text-xs text-red-600 mt-2">
                ⚠ Certaines positions sortent du mur ({maxX}×{maxY}) — vérifiez l'offset.
              </p>
            )}

            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-slate-500">
                {applying ? `Envoi ${progress}/${preview.length}…` : `${preview.length} prix sélectionné(s)`}
              </span>
              <div className="flex gap-2">
                <button onClick={onClose} disabled={applying}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100">Annuler</button>
                <button onClick={handleApply} disabled={applying || preview.length === 0}
                  className="px-5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                  {applying ? 'Déplacement…' : 'Déplacer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMoveArticles;
